import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const TABS = [
  { key: 'calls', label: 'Calls', icon: 'bi-telephone' },
  { key: 'contacts', label: 'Contacts', icon: 'bi-people' },
  { key: 'today', label: 'Today Follow-ups', icon: 'bi-calendar-day' },
  { key: 'pending', label: 'Pending Follow-ups', icon: 'bi-calendar-check' },
  { key: 'upcoming', label: 'Upcoming Follow-ups', icon: 'bi-clock-history' },
  { key: 'missed', label: 'Missed Follow-ups', icon: 'bi-exclamation-triangle' },
];

const emptyContactForm = { id: '', name: '', phone: '', email: '', businessName: '', source: 'manual' };
const emptyCallForm = {
  id: '',
  name: '',
  phone: '',
  email: '',
  businessName: '',
  source: 'manual',
  callType: 'outgoing',
  callStatus: '',
  note: ''
};
const emptyFollowUpForm = { id: '', callId: '', outcome: 'callback', note: '', callStatus: '', nextFollowDateTime: '' };

  const badgeByOutcome = {
    interested: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    not_interested: 'bg-rose-100 text-rose-800 border-rose-300',
    callback: 'bg-amber-100 text-amber-800 border-amber-300',
    no_answer: 'bg-slate-200 text-slate-800 border-slate-300'
  };

const outcomeLabel = {
  interested: 'Interested',
  not_interested: 'Not Interested',
  callback: 'Callback',
  no_answer: 'No Answer'
};

const CallLogs = () => {
  const [activeTab, setActiveTab] = useState('calls');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    callsThisMonth: 0,
    todayCalls: 0,
    todayFollowUps: 0,
    upcomingFollowUps: 0,
    pendingFollowUps: 0,
    missedFollowUps: 0,
    totalCalls: 0,
    interestedCount: 0,
    notInterestedCount: 0,
    callLaterCount: 0,
    noAnswerCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [filters, setFilters] = useState({ search: '', outcome: '', callStatus: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedCallDetails, setSelectedCallDetails] = useState(null);
  const [detailsFollowUps, setDetailsFollowUps] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [callForm, setCallForm] = useState(emptyCallForm);
  const [followUpForm, setFollowUpForm] = useState(emptyFollowUpForm);
  const [deleteState, setDeleteState] = useState({ type: '', id: '' });
  const [vendorKeyword, setVendorKeyword] = useState('');
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await apiService.get('call/dashboard-stats');
      if (response?.status) {
        setStats(response?.data?.stats || {});
      }
    } catch (error) {
      console.error('Failed to load call dashboard stats:', error);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage
      };

      let endpoint = 'call/list';
      if (activeTab === 'contacts') endpoint = 'contact/list';
      if (activeTab === 'today') endpoint = 'call/followup/today';
      if (activeTab === 'pending') endpoint = 'call/followup/pending';
      if (activeTab === 'upcoming') endpoint = 'call/followup/upcoming';
      if (activeTab === 'missed') endpoint = 'call/followup/missed';

      if (activeTab === 'calls' || activeTab === 'contacts') {
        payload.search = filters.search;
      }
      if (activeTab === 'calls') {
        payload.callStatus = filters.callStatus;
        payload.outcome = filters.outcome;
      }
      if (activeTab === 'today' || activeTab === 'pending' || activeTab === 'upcoming' || activeTab === 'missed') {
        payload.outcome = filters.outcome;
        payload.callStatus = filters.callStatus;
      }

      const response = await apiService.post(endpoint, payload);
      setRows(response?.data || []);
      setTotalRecords(response?.recordsFiltered ?? response?.recordsTotal ?? 0);
    } catch (error) {
      console.error('Failed to load call management data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, recordsPerPage, filters.search, filters.outcome, filters.callStatus]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!callModalOpen || callForm.id) return;

    const keyword = vendorKeyword.trim();
    if (!keyword) {
      setVendorOptions([]);
      setVendorLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setVendorLoading(true);
        const response = await apiService.post('call/vendor-search', { keyword });
        setVendorOptions(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        setVendorOptions([]);
      } finally {
        setVendorLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [callModalOpen, callForm.id, vendorKeyword]);

  const onTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const openCreateContact = () => {
    setContactForm(emptyContactForm);
    setContactModalOpen(true);
  };

  const openEditContact = (contact) => {
    setContactForm({
      id: contact?._id || '',
      name: contact?.name || '',
      phone: contact?.phone || '',
      email: contact?.email || '',
      businessName: contact?.businessName || '',
      source: contact?.source || 'manual'
    });
    setContactModalOpen(true);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    try {
      if (contactForm.id) {
        await apiService.put(`contact/update/${contactForm.id}`, contactForm);
        toast.success('Contact updated successfully');
      } else {
        await apiService.post('contact/create', contactForm);
        toast.success('Contact created successfully');
      }
      setContactModalOpen(false);
      fetchRows();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save contact');
    }
  };

  const findCallByPhone = async (phone) => {
    try {
      const response = await apiService.post('call/list', { start: 0, length: 1, search: phone });
      const calls = Array.isArray(response?.data) ? response.data : [];
      return calls.length > 0 ? calls[0] : null;
    } catch (error) {
      return null;
    }
  };

  const openCreateCall = () => {
    setCallForm(emptyCallForm);
    setVendorKeyword('');
    setVendorOptions([]);
    setVendorLoading(false);
    setCallModalOpen(true);
  };

  const selectVendorForCall = (vendor) => {
    const vendorName = [vendor?.first_name, vendor?.last_name].filter(Boolean).join(' ').trim();

    setCallForm((prev) => ({
      ...prev,
      name: vendorName || prev.name || '',
      phone: vendor?.phone ? String(vendor.phone) : '',
      email: vendor?.email || '',
      businessName: vendor?.business_name || '',
      source: 'vendor'
    }));
    setVendorKeyword('');
    setVendorOptions([]);
  };

  const openEditCall = (call) => {
    const rawCall = call?.rawData || call;
    setVendorKeyword('');
    setVendorOptions([]);
    setVendorLoading(false);
    setCallForm({
      id: rawCall?._id || '',
      name: rawCall?.contactId?.name || '',
      phone: rawCall?.contactId?.phone || '',
      email: rawCall?.contactId?.email || '',
      businessName: rawCall?.contactId?.businessName || '',
      source: rawCall?.contactId?.source || 'manual',
      callType: rawCall?.callType || 'outgoing',
      callStatus: rawCall?.lastFollowUp?.callStatus || '',
      note: rawCall?.note || '',
      nextFollowDateTime: rawCall?.lastFollowUp?.nextFollowDateTime || rawCall?.lastFollowUp?.followUpDateTime || '',
      followUpOutcome: rawCall?.lastFollowUp?.outcome || ''
    });
    setCallModalOpen(true);
  };

    const saveCall = async (e) => {
    e.preventDefault();
    try {
      const isCreate = !callForm.id;
      
      if (isCreate && callForm.followUpOutcome === 'callback' && !callForm.nextFollowDateTime) {
        toast.error('Next follow-up date and time is required for callback outcome');
        return;
      }

      // Check if this phone number already has an existing call
      if (isCreate && callForm.phone) {
        const existingCall = await findCallByPhone(callForm.phone);
        if (existingCall) {
          // Load the existing call for editing instead of creating a duplicate
          setCallForm({
            id: existingCall._id || '',
            name: existingCall.contactName || existingCall.contactId?.name || callForm.name,
            phone: callForm.phone,
            email: existingCall.contactEmail || existingCall.contactId?.email || callForm.email,
            businessName: existingCall.businessName || existingCall.contactId?.businessName || callForm.businessName,
            source: existingCall.source || callForm.source || 'manual',
            callType: existingCall.callType || 'outgoing',
            callStatus: existingCall.lastFollowUp?.callStatus || '',
            note: existingCall.note || '',
            nextFollowDateTime: existingCall.lastFollowUp?.nextFollowDateTime || callForm.nextFollowDateTime,
            followUpOutcome: existingCall.lastFollowUp?.outcome || callForm.followUpOutcome
          });
          toast.info('This contact already has a call. Opening edit mode...');
          return;
        }
      }

      const payload = {
        name: callForm.name,
        phone: callForm.phone,
        email: callForm.email,
        businessName: callForm.businessName,
        source: callForm.source,
        callType: callForm.callType,
      };

      if (isCreate) {
        payload.note = callForm.note;
        payload.nextFollowDateTime = callForm.nextFollowDateTime;
        payload.outcome = callForm.followUpOutcome ? callForm.followUpOutcome : undefined;
        payload.callStatus = callForm.callStatus || undefined;
      }

      if (callForm.id) {
        await apiService.put(`call/update/${callForm.id}`, payload);
        toast.success('Call updated successfully');
      } else {
        await apiService.post('call/create', payload);
        toast.success('Call created successfully');
      }
      setCallModalOpen(false);
      fetchRows();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save call');
    }
  };

  const openCreateFollowUp = (callId) => {
    setFollowUpForm({ ...emptyFollowUpForm, callId });
    setFollowUpModalOpen(true);
  };

  const openEditFollowUp = (followUp) => {
    setFollowUpForm({
      id: followUp?._id || '',
      callId: followUp?.callId?._id || followUp?.callId || '',
      outcome: followUp?.outcome || '',
      note: followUp?.note || '',
      callStatus: followUp?.callStatus || '',
      nextFollowDateTime: followUp?.nextFollowDateTime || followUp?.followUpDateTime || ''
    });
    setFollowUpModalOpen(true);
  };

  const openCallDetails = async (call) => {
    const rawCall = call?.rawData || call;
    setSelectedCallDetails(rawCall);
    setDetailsModalOpen(true);
    setDetailsFollowUps([]);
    setDetailsLoading(true);
    try {
      const response = await apiService.post('call/followup/list', { start: 0, length: 200 });
      const allFollowUps = Array.isArray(response?.data) ? response.data : [];
      const filtered = allFollowUps.filter((item) => item?.callId?._id === rawCall?._id);
      await refreshDetailsFollowUps(rawCall?._id);
    } catch (error) {
      toast.error('Failed to load follow-up history');
    } finally {
      setDetailsLoading(false);
    }
  };


  const refreshDetailsFollowUps = async (callId) => {
    if (!callId) return;
    try {
      const response = await apiService.post('call/followup/list', { start: 0, length: 200 });
      const allFollowUps = Array.isArray(response?.data) ? response.data : [];
      const filtered = allFollowUps.filter((item) => item?.callId?._id === callId);
      setDetailsFollowUps(filtered);
    } catch (error) {
      toast.error('Failed to refresh follow-up history');
    }
  };

  const saveFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.callId) {
      toast.error('Call is required');
      return;
    }
    try {
      if (followUpForm.outcome === 'callback' && !followUpForm.nextFollowDateTime) {
        toast.error('Next follow-up date and time is required for callback outcome');
        return;
      }
      if (followUpForm.id) {
        await apiService.put(`call/followup/update/${followUpForm.id}`, followUpForm);
        toast.success('Follow-up updated successfully');
      } else {
        await apiService.post('call/followup/create', followUpForm);
        toast.success('Follow-up created successfully');
      }
      setFollowUpModalOpen(false);
      setFollowUpForm(emptyFollowUpForm);
      await refreshDetailsFollowUps(followUpForm.callId);
      fetchRows();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create follow-up');
    }
  };

  const openDelete = (type, id) => {
    setDeleteState({ type, id });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteState.type === 'contact') {
        await apiService.delete(`contact/delete/${deleteState.id}`);
      } else if (deleteState.type === 'followup') {
        await apiService.delete(`call/followup/delete/${deleteState.id}`);
      } else {
        await apiService.delete(`call/delete/${deleteState.id}`);
      }
      toast.success(`${deleteState.type === 'contact' ? 'Contact' : deleteState.type === 'followup' ? 'Follow-up' : 'Call'} deleted successfully`);
      setDeleteModalOpen(false);
      if (deleteState.type === 'followup') {
        await refreshDetailsFollowUps(selectedCallDetails?._id);
      }
      fetchRows();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const showSearch = activeTab === 'calls' || activeTab === 'contacts';
  const showOutcome = activeTab === 'calls' || activeTab === 'today' || activeTab === 'pending' || activeTab === 'upcoming' || activeTab === 'missed' || activeTab === 'completed';
  const showCallStatus = activeTab === 'calls' || activeTab === 'today' || activeTab === 'pending' || activeTab === 'upcoming' || activeTab === 'missed' || activeTab === 'completed';

  const ctaLabel = useMemo(() => {
    if (activeTab === 'contacts') return 'Add Contact';
    if (activeTab === 'calls') return 'Add Call';
    return null;
  }, [activeTab]);

  const formatDateTime = (value) => {
    if (!value) return '--';
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLabel = (value) => {
    if (!value) return "--";
    return value.toString().replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatAdminName = (admin) => {
    if (!admin) return '--';
    const fullName = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
    return fullName || admin.email || '--';
  };

  const hasMeaningfulBusinessName = (value) => {
    if (!value) return false;
    const normalized = value.toString().trim().toLowerCase();
    return normalized !== '' && normalized !== 'not specified' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'null' && normalized !== 'undefined';
  };

  const renderContactDetails = (contact) => (
    <div className="text-[11px] leading-[1.05]">
      {(contact?.name || contact?.businessName) && (
        <p className="font-semibold text-gray-800 leading-[1.05]">
          {contact?.name
            ? `${contact.name}${hasMeaningfulBusinessName(contact?.businessName) ? ` (${contact.businessName})` : ''}`
            : contact.businessName}
        </p>
      )}
      {contact?.phone && <p className="text-gray-600 leading-[1.05]">Phone: {contact.phone}</p>}
      {contact?.email && <p className="text-gray-500 leading-[1.05]">Email: {contact.email}</p>}
      {!contact?.name && !contact?.businessName && !contact?.phone && !contact?.email && (
        <p className="text-gray-500 leading-[1.05]">--</p>
      )}
    </div>
  );

  const renderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {/* Total Calls Card */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-blue-500 rounded-lg">
            <i className="bi bi-telephone text-white text-lg"></i>
          </div>
          <span className="text-xs text-blue-600 font-medium bg-blue-200 px-2 py-1 rounded-full">Total</span>
        </div>
        <p className="text-[11px] text-blue-600 uppercase tracking-wide font-semibold">Total Calls</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-blue-900">{stats.totalCalls || 0}</span>
          <span className="text-xs text-blue-700">all time</span>
        </div>
      </div>

      {/* This Month Calls Card */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-emerald-500 rounded-lg">
            <i className="bi bi-calendar-month text-white text-lg"></i>
          </div>
          <span className="text-xs text-emerald-600 font-medium bg-emerald-200 px-2 py-1 rounded-full">Month</span>
        </div>
        <p className="text-[11px] text-emerald-600 uppercase tracking-wide font-semibold">This Month</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-emerald-900">{stats.callsThisMonth || 0}</span>
          <span className="text-xs text-emerald-700">calls</span>
        </div>
      </div>

      {/* Today Calls Card */}
      <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-violet-500 rounded-lg">
            <i className="bi bi-calendar-day text-white text-lg"></i>
          </div>
          <span className="text-xs text-violet-600 font-medium bg-violet-200 px-2 py-1 rounded-full">Today</span>
        </div>
        <p className="text-[11px] text-violet-600 uppercase tracking-wide font-semibold">Today's Calls</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-violet-900">{stats.todayCalls || 0}</span>
          <span className="text-xs text-violet-700">calls</span>
        </div>
      </div>

      {/* Today Follow-ups Card */}
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-indigo-500 rounded-lg">
            <i className="bi bi-clock-history text-white text-lg"></i>
          </div>
          <span className="text-xs text-indigo-600 font-medium bg-indigo-200 px-2 py-1 rounded-full">Today</span>
        </div>
        <p className="text-[11px] text-indigo-600 uppercase tracking-wide font-semibold">Today Follow-ups</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-indigo-900">{stats.todayFollowUps || 0}</span>
          <span className="text-xs text-indigo-700">scheduled</span>
        </div>
      </div>

      {/* Upcoming Follow-ups Card */}
      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-cyan-500 rounded-lg">
            <i className="bi bi-calendar-check text-white text-lg"></i>
          </div>
          <span className="text-xs text-cyan-600 font-medium bg-cyan-200 px-2 py-1 rounded-full">Upcoming</span>
        </div>
        <p className="text-[11px] text-cyan-600 uppercase tracking-wide font-semibold">Upcoming Follow-ups</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-cyan-900">{stats.upcomingFollowUps || 0}</span>
          <span className="text-xs text-cyan-700">scheduled</span>
        </div>
      </div>

      {/* Pending Follow-ups Card */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-amber-500 rounded-lg">
            <i className="bi bi-hourglass-split text-white text-lg"></i>
          </div>
          <span className="text-xs text-amber-600 font-medium bg-amber-200 px-2 py-1 rounded-full">Pending</span>
        </div>
        <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold">Pending Follow-ups</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-amber-900">{stats.pendingFollowUps || 0}</span>
          <span className="text-xs text-amber-700">scheduled</span>
        </div>
      </div>

      {/* Missed Follow-ups Card */}
      <div className="bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-rose-500 rounded-lg">
            <i className="bi bi-exclamation-triangle text-white text-lg"></i>
          </div>
          <span className="text-xs text-rose-600 font-medium bg-rose-200 px-2 py-1 rounded-full">Missed</span>
        </div>
        <p className="text-[11px] text-rose-600 uppercase tracking-wide font-semibold">Missed Follow-ups</p>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-2xl font-bold text-rose-900">{stats.missedFollowUps || 0}</span>
          <span className="text-xs text-rose-700">missed</span>
        </div>
      </div>

      {/* Call Outcomes Summary Card */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 bg-slate-500 rounded-lg">
            <i className="bi bi-pie-chart text-white text-lg"></i>
          </div>
          <span className="text-xs text-slate-600 font-medium bg-slate-200 px-2 py-1 rounded-full">Summary</span>
        </div>
        <p className="text-[11px] text-slate-600 uppercase tracking-wide font-semibold">Call Outcomes</p>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-emerald-700 flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              Interested
            </span>
            <span className="font-semibold text-emerald-900">{stats.interestedCount || 0}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-rose-700 flex items-center gap-1">
              <span className="w-2 h-2 bg-rose-500 rounded-full"></span>
              Not Interested
            </span>
            <span className="font-semibold text-rose-900">{stats.notInterestedCount || 0}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-amber-700 flex items-center gap-1">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              Callback
            </span>
            <span className="font-semibold text-amber-900">{stats.callLaterCount || 0}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-700 flex items-center gap-1">
              <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
              No Answer
            </span>
            <span className="font-semibold text-slate-900">{stats.noAnswerCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );

    const renderCallsTable = () => (
    <table className="w-full min-w-[1150px] text-sm">
      <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
        <tr>
          <th className="px-2 py-2 w-[60px] min-w-[60px]">#</th>
          <th className="px-2 py-2 w-[260px] min-w-[260px]">Contact Details</th>
          <th className="px-2 py-2 w-[320px] min-w-[320px]">Status</th>
          <th className="px-2 py-2 w-[160px] min-w-[160px]">Next Follow-up</th>
          <th className="px-2 py-2 w-[120px] min-w-[120px] text-center">Actions</th>
          <th className="px-2 py-2 w-[200px] min-w-[200px]">Created/Updated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.map((call, idx) => {
          const rawCall = call?.rawData || call;
          const contactDetails = {
            name: call?.contactName || rawCall?.contactId?.name,
            phone: call?.contactPhone || rawCall?.contactId?.phone,
            email: call?.contactEmail || rawCall?.contactId?.email,
            businessName: call?.businessName || rawCall?.contactId?.businessName
          };
          const rowToneClass = (rawCall?.callType || call?.callType) === 'incoming' ? 'bg-sky-50/60 hover:bg-sky-100/60' : 'bg-violet-50/50 hover:bg-violet-100/60';
          return (
            <tr
              key={call._id}
              className={`${rowToneClass} cursor-pointer`}
              onClick={() => openCallDetails(call)}
            >
              <td className="px-2 py-2">{(currentPage - 1) * recordsPerPage + idx + 1}</td>
              <td className="px-2 py-2">
                {renderContactDetails(contactDetails)}
              </td>
              <td className="px-2 py-2 align-top">
                {(() => {
                  const statusValue = call?.lastCallStatus || (rawCall?.lastFollowUp?.callStatus ? formatLabel(rawCall?.lastFollowUp?.callStatus) : "--");
                  const rawStatus = rawCall?.lastFollowUp?.callStatus || "";
                  const outcomeValue = call?.lastOutcome || (rawCall?.lastFollowUp?.outcome ? formatLabel(outcomeLabel[rawCall.lastFollowUp.outcome] || rawCall.lastFollowUp.outcome) : "--");
                  const noteValue = call?.lastNote || rawCall?.lastFollowUp?.note || rawCall?.note || "--";

                  const statusConfig = {
                    answered: 'bg-emerald-50 text-emerald-700 border-emerald-300',
                    missed: 'bg-rose-50 text-rose-700 border-rose-300',
                    busy: 'bg-amber-50 text-amber-700 border-amber-300',
                    not_connected: 'bg-slate-50 text-slate-700 border-slate-300',
                    invalid: 'bg-orange-50 text-orange-700 border-orange-300',
                    disconnected: 'bg-red-50 text-red-700 border-red-300',

                  };
                  const statusClass = statusConfig[rawStatus] || 'bg-slate-50 text-slate-700 border-slate-300';

                  return (
                    <div className="space-y-2 text-[11px] text-slate-700">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-500">Call Status:</span>
                        <span className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full ${statusClass} font-semibold text-[10px]`}>
                          {statusValue}
                        </span>
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] bg-violet-100 text-violet-700 font-semibold">
                          {call?.callType || formatLabel(rawCall?.callType)}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-500">Outcome:</span>{" "}
                        {rawCall?.lastFollowUp?.outcome ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeByOutcome[rawCall.lastFollowUp.outcome] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                            {outcomeLabel[rawCall.lastFollowUp.outcome] || formatLabel(rawCall.lastFollowUp.outcome)}
                          </span>
                        ) : (
                          <span>{outcomeValue}</span>
                        )}
                      </div>
                      <div className="whitespace-pre-wrap break-words">
                        <span className="font-semibold text-slate-500">Note:</span>{" "}
                        <span>{noteValue}</span>
                      </div>
                    </div>
                  );
                })()}
              </td>
              <td className="px-2 py-2 text-[11px]">
                {(() => {
                  const nextDate = call?.nextFollowUpDate || formatDateTime(rawCall?.lastFollowUp?.nextFollowDateTime || rawCall?.lastFollowUp?.followUpDateTime);
                  const nextLabel = nextDate && nextDate !== '--' ? nextDate : '--';
                  return (
                    <div className="flex flex-col gap-1 items-center">
                      <div className="text-[11px] text-slate-600 truncate text-center w-full">{nextLabel}</div>
                    </div>
                  );
                })()}
              </td>
              <td className="px-2 py-2">
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreateFollowUp(call._id);
                    }}
                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-md hover:bg-emerald-200 text-center whitespace-nowrap min-w-[90px]"
                  >
                    <i className="bi bi-calendar2-check mr-1"></i>
                    Follow-up
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditCall(call);
                    }}
                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-center min-w-[90px]"
                  >
                    <i className="bi bi-pencil-square mr-1"></i>
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDelete('call', call._id);
                    }}
                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-rose-100 text-rose-800 rounded-md hover:bg-rose-200 text-center min-w-[90px]"
                  >
                    <i className="bi bi-trash mr-1"></i>
                    Delete
                  </button>
                </div>
              </td>
              <td className="px-2 py-2 text-[11px] text-slate-600">
                <div className="flex flex-col gap-1">
                  <div><span className="text-slate-500">Created:</span><br />{formatDateTime(rawCall?.createdAt)}</div>
                  <div><span className="text-slate-500">Updated:</span><br />{formatDateTime(rawCall?.updatedAt)}</div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  const renderContactsTable = () => (
    <table className="w-full min-w-[900px] text-sm">
      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-2 py-2 w-[60px] min-w-[60px]">#</th>
          <th className="px-2 py-2 w-[280px] min-w-[280px]">Contact Details</th>
          <th className="px-2 py-2 w-[100px] min-w-[100px]">Source</th>
          <th className="px-2 py-2 w-[200px] min-w-[200px]">Created/Updated</th>
          <th className="px-2 py-2 w-[150px] min-w-[150px] text-center">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.map((contact, idx) => (
          <tr key={contact._id} className="hover:bg-gray-50">
            <td className="px-2 py-2">{(currentPage - 1) * recordsPerPage + idx + 1}</td>
            <td className="px-2 py-2">{renderContactDetails(contact)}</td>
            <td className="px-2 py-2">
              <span className="px-2 py-1 bg-sky-100 text-sky-700 rounded-full text-xs">{contact?.source || 'manual'}</span>
            </td>
            <td className="px-2 py-2 text-[11px] text-slate-600">
              <div className="flex flex-col gap-1">
                <div><span className="text-slate-500">Created:</span><br />{formatDateTime(contact?.createdAt)}</div>
                <div><span className="text-slate-500">Updated:</span><br />{formatDateTime(contact?.updatedAt)}</div>
              </div>
            </td>
            <td className="px-2 py-2">
  <div className="flex flex-col items-center gap-2">
    <button
      onClick={() => openEditContact(contact)}
      className="inline-block px-2 py-1 text-[11px] font-semibold bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-center min-w-[90px] w-full"
    >
      <i className="bi bi-pencil-square mr-1"></i>
      Edit
    </button>
    <button
      onClick={() => {
        setCallForm({
          id: '',
          name: contact?.name || '',
          phone: contact?.phone || '',
          email: contact?.email || '',
          businessName: contact?.businessName || '',
          source: contact?.source || 'manual',
          callType: 'outgoing',
          callStatus: '',
          note: '',
          nextFollowDateTime: '',
          followUpOutcome: 'callback'
        });
        setCallModalOpen(true);
      }}
      className="inline-block px-2 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-md hover:bg-emerald-200 text-center whitespace-nowrap min-w-[90px] w-full"
    >
      <i className="bi bi-plus-circle mr-1"></i>
      New Call
    </button>
    <button
      onClick={() => openDelete('contact', contact._id)}
      className="inline-block px-2 py-1 text-[11px] font-semibold bg-rose-100 text-rose-800 rounded-md hover:bg-rose-200 text-center min-w-[90px] w-full"
    >
      <i className="bi bi-trash mr-1"></i>
      Delete
    </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderFollowupTable = () => (
    <table className="w-full min-w-[1150px] text-sm">
      <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
        <tr>
          <th className="px-2 py-2 w-[60px] min-w-[60px]">#</th>
          <th className="px-2 py-2 w-[260px] min-w-[260px]">Contact Details</th>
          <th className="px-2 py-2 w-[320px] min-w-[320px]">Status</th>
          <th className="px-2 py-2 w-[180px] min-w-[180px]">Follow-up Date</th>
          <th className="px-2 py-2 w-[160px] min-w-[160px]">Created By</th>
          <th className="px-2 py-2 w-[120px] min-w-[120px] text-center">Actions</th>
          <th className="px-2 py-2 w-[200px] min-w-[200px]">Created/Updated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.map((item, idx) => {
          const linkedCall = item?.callId?.rawData || item?.callId;
          const contactDetails = item?.contactId || linkedCall?.contactId || {};
          const rawCallStatus = item?.callStatus || linkedCall?.lastFollowUp?.callStatus || '';
          const statusConfig = {
            answered: 'bg-emerald-50 text-emerald-700 border-emerald-300',
            missed: 'bg-rose-50 text-rose-700 border-rose-300',
            busy: 'bg-amber-50 text-amber-700 border-amber-300',
            not_connected: 'bg-slate-50 text-slate-700 border-slate-300',
            invalid: 'bg-orange-50 text-orange-700 border-orange-300',
            disconnected: 'bg-red-50 text-red-700 border-red-300'
                    };
          const callStatusClass = statusConfig[rawCallStatus] || 'bg-slate-50 text-slate-700 border-slate-300';
          const linkedCallId = linkedCall?._id;
          const rowToneClass = linkedCall?.callType === 'incoming' ? 'bg-sky-50/60 hover:bg-sky-100/60' : 'bg-violet-50/50 hover:bg-violet-100/60';

          return (
            <tr
              key={item._id}
              className={`${rowToneClass} cursor-pointer`}
              onClick={() => linkedCall && openCallDetails(linkedCall)}
            >
              <td className="px-2 py-2">{(currentPage - 1) * recordsPerPage + idx + 1}</td>
              <td className="px-2 py-2">{renderContactDetails(contactDetails)}</td>
              <td className="px-2 py-2 align-top">
                <div className="space-y-2 text-[11px] text-slate-700">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-500">Call Status:</span>
                    <span className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full ${callStatusClass} font-semibold text-[10px]`}>
                      {formatLabel(rawCallStatus)}
                    </span>
                    {linkedCall?.callType && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] bg-violet-100 text-violet-700 font-semibold">
                        {formatLabel(linkedCall.callType)}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-500">Outcome:</span>{" "}
                    {item?.outcome ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeByOutcome[item.outcome] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                        {outcomeLabel[item.outcome] || formatLabel(item.outcome)}
                      </span>
                    ) : (
                      <span>--</span>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap break-words">
                    <span className="font-semibold text-slate-500">Note:</span>{" "}
                    <span>{item?.note || linkedCall?.note || '--'}</span>
                  </div>
                </div>
              </td>
              <td className="px-2 py-2 text-[11px] text-slate-600">
                <div className="flex flex-col gap-1 items-center">
                  <div className="text-center w-full">{formatDateTime(item?.nextFollowDateTime || item?.followUpDateTime)}</div>
                </div>
              </td>
              <td className="px-2 py-2 text-[11px] text-slate-600">
                <div className="flex flex-col gap-1">
                  <div>{formatAdminName(item?.createdBy)}</div>
                </div>
              </td>
              <td className="px-2 py-2">
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (linkedCallId) openCreateFollowUp(linkedCallId);
                    }}
                    disabled={!linkedCallId}
                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-md hover:bg-emerald-200 text-center whitespace-nowrap min-w-[90px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="bi bi-calendar2-check mr-1"></i>
                    Follow-up
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (linkedCall) openCallDetails(linkedCall);
                    }}
                    disabled={!linkedCall}
                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-center min-w-[90px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="bi bi-eye mr-1"></i>
                    View
                  </button>
                </div>
              </td>
              <td className="px-2 py-2 text-[11px] text-slate-600">
                <div className="flex flex-col gap-1">
                  <div><span className="text-slate-500">Created:</span><br />{formatDateTime(item?.createdAt)}</div>
                  <div><span className="text-slate-500">Updated:</span><br />{formatDateTime(item?.updatedAt)}</div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  const renderTable = () => {
    if (activeTab === 'calls') return renderCallsTable();
    if (activeTab === 'contacts') return renderContactsTable();
    return renderFollowupTable();
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen">
      <div className="mb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Call Management</h1>
            <p className="text-sm text-slate-500 mt-1">Analytics, calls, contacts and follow-up tracking</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAnalytics((prev) => !prev)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 bg-white text-xs text-slate-600"
            >
              <span className={`inline-flex h-4 w-8 items-center rounded-full ${showAnalytics ? 'bg-indigo-500' : 'bg-slate-200'}`}>
                <span className={`h-3 w-3 rounded-full bg-white shadow-sm transition ${showAnalytics ? 'translate-x-4' : 'translate-x-1'}`}></span>
              </span>
              Show analytics
            </button>
            {ctaLabel && (
              <button
                onClick={activeTab === 'contacts' ? openCreateContact : openCreateCall}
                className="px-4 py-2 rounded-full bg-indigo-600 text-white text-sm font-medium shadow-sm"
              >
                <i className="bi bi-plus-lg mr-1"></i>{ctaLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {showAnalytics && renderStats()}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-4 mb-4">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                className={`px-2 py-2 text-xs font-semibold border-b-2 transition ${activeTab === tab.key ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              {showSearch && (
                <div className="flex-1">
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Search</label>
                  <div className="relative">
                    <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => onFilterChange('search', e.target.value)}
                      placeholder={activeTab === 'calls' ? 'Name, phone, email' : 'Search contacts'}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}


              {showCallStatus && (
                <div className="flex-1 md:flex-initial">
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Call Status</label>
                  <select
                    value={filters.callStatus}
                    onChange={(e) => onFilterChange('callStatus', e.target.value)}
                    className="w-full md:w-40 px-3 py-2.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Call Status</option>
                    <option value="answered">Answered</option>
                    <option value="missed">Missed</option>
                    <option value="busy">Busy</option>
                    <option value="not_connected">Not Connected</option>
                    <option value="invalid">Invalid</option>
                    <option value="disconnected">Disconnected</option>

                  </select>
                </div>
              )}

              {showOutcome && (
                <div className="flex-1 md:flex-initial">
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Outcome</label>
                  <select
                    value={filters.outcome}
                    onChange={(e) => onFilterChange('outcome', e.target.value)}
                    className="w-full md:w-40 px-3 py-2.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Outcomes</option>
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="callback">Callback</option>
                    <option value="no_answer">No Answer</option>
                  </select>
                </div>
              )}

              {ctaLabel && (
                <div className="flex-shrink-0 md:pt-4">
                  <button
                    onClick={activeTab === 'contacts' ? openCreateContact : openCreateCall}
                    className="w-full md:w-auto px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition"
                  >
                    <i className="bi bi-plus-lg mr-1"></i>{ctaLabel}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-2">
          {loading ? (
            <div className="py-16 text-center">
              <div className="inline-block h-8 w-8 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin"></div>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-gray-500">No records found</div>
          ) : (
            <div className="overflow-x-auto">{renderTable()}</div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="px-3 py-2 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={recordsPerPage}
                onChange={(e) => {
                  setRecordsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              <span className="text-sm text-gray-600">{currentPage}/{totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {contactModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{contactForm.id ? 'Edit Contact' : 'Create Contact'}</h3>
                <p className="text-[11px] text-slate-500">Add or update contact information</p>
              </div>
              <button type="button" onClick={() => setContactModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={saveContact} className="px-3 py-2.5 space-y-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Contact Name *</label>
                  <input type="text" required placeholder="Enter contact name" value={contactForm.name} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009ef7]/30 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Business Name</label>
                  <input type="text" placeholder="Enter business name" value={contactForm.businessName} onChange={(e) => setContactForm((p) => ({ ...p, businessName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009ef7]/30 focus:border-transparent" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Phone Number *</label>
                    <input type="text" required placeholder="Enter phone number" value={contactForm.phone} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009ef7]/30 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Email Address</label>
                    <input type="email" placeholder="Enter email address" value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009ef7]/30 focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Contact Source</label>
                  <select value={contactForm.source} onChange={(e) => setContactForm((p) => ({ ...p, source: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009ef7]/30 focus:border-transparent bg-white">
                    <option value="manual">Manual</option>
                    <option value="vendor">Vendor</option>
                    <option value="user">User</option>
                    <option value="lead">Lead</option>
                  </select>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 sticky bottom-0 bg-white border-t border-slate-100 px-3 py-2.5 -mx-3 -mb-2.5">
                  <button type="button" onClick={() => setContactModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-3 py-2 text-sm bg-[#009ef7] text-white rounded-lg">{followUpForm.id ? 'Update' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <>
        {callModalOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{callForm.id ? "Edit Call" : "Create Call"}</h3>
                  <p className="text-[11px] text-slate-500">Add call details and the next follow-up</p>
                </div>
                <button type="button" onClick={() => setCallModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                  <i className="bi bi-x"></i>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <form onSubmit={saveCall} className="px-3 py-2.5 space-y-3">
                {!callForm.id && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[11px] text-slate-500">Type a keyword to find a vendor and autofill call details.</p>
                      </div>
                      <span className="text-[10px] text-slate-400">Max 25</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by name, business, email or phone"
                        value={vendorKeyword}
                        onChange={(e) => setVendorKeyword(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#009ef7]/30"
                      />
                      {vendorLoading && (
                        <div className="mt-2 text-[11px] text-slate-500">Searching vendors...</div>
                      )}
                      {!vendorLoading && vendorKeyword.trim() && vendorOptions.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                          {vendorOptions.map((vendor) => {
                            const vendorName = [vendor?.first_name, vendor?.last_name].filter(Boolean).join(' ').trim();
                            return (
                              <button
                                key={vendor._id}
                                type="button"
                                onClick={() => selectVendorForCall(vendor)}
                                className="w-full px-3 py-2.5 text-left border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                              >
                                <div className="text-sm font-medium text-slate-800">{vendorName || 'Unnamed Vendor'}</div>
                                <div className="text-[11px] text-slate-500">
                                  {[vendor?.business_name, vendor?.phone, vendor?.email].filter(Boolean).join(' | ')}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {!vendorLoading && vendorKeyword.trim() && vendorOptions.length === 0 && (
                        <div className="mt-2 text-[11px] text-slate-500">No vendors found for this keyword.</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Contact Name *</label>
                    <input type="text" required placeholder="Enter contact name" value={callForm.name} onChange={(e) => setCallForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Business Name</label>
                    <input type="text" placeholder="Enter business name" value={callForm.businessName} onChange={(e) => setCallForm((p) => ({ ...p, businessName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Phone Number *</label>
                    <input type="text" required placeholder="Enter phone number" value={callForm.phone} onChange={(e) => setCallForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Email Address</label>
                    <input type="email" placeholder="Enter email address" value={callForm.email} onChange={(e) => setCallForm((p) => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Call Type</label>
                  <select value={callForm.callType} onChange={(e) => setCallForm((p) => ({ ...p, callType: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="outgoing">Outgoing Call</option>
                    <option value="incoming">Incoming Call</option>
                  </select>
                </div>
                {!callForm.id && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                    <p className="text-[11px] font-semibold text-slate-600 mb-2">📅 Follow-up Details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Call Status</label>
                        <select value={callForm.callStatus} onChange={(e) => setCallForm((p) => ({ ...p, callStatus: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                          <option value="">Select call status</option>
                          <option value="answered">Answered</option>
                          <option value="missed">Missed</option>
                          <option value="busy">Busy</option>
                          <option value="not_connected">Not Connected</option>
                          <option value="invalid">Invalid Number</option>
                          <option value="disconnected">Disconnected</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Call Outcome</label>
                        <select value={callForm.followUpOutcome} onChange={(e) => setCallForm((p) => ({ ...p, followUpOutcome: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                          <option value="">Select call outcome</option>
                          <option value="interested">Interested</option>
                          <option value="not_interested">Not Interested</option>
                          <option value="callback">Callback</option>
                          <option value="no_answer">No Answer</option>
                        </select>
                      </div>
                    </div>
                    {callForm.followUpOutcome === 'callback' && (
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Next Follow-up Date & Time *</label>
                        <input type="datetime-local" required value={callForm.nextFollowDateTime} onChange={(e) => setCallForm((p) => ({ ...p, nextFollowDateTime: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
                      </div>
                    )}
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Call Notes</label>
                      <textarea rows="3" placeholder="Add follow-up notes..." value={callForm.note} onChange={(e) => setCallForm((p) => ({ ...p, note: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-2 sticky bottom-0 bg-white border-t border-slate-100 px-3 py-2.5 -mx-3 -mb-2.5">
                  <button type="button" onClick={() => setCallModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="px-3 py-2 text-sm bg-[#009ef7] text-white rounded-lg">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {followUpModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[10050] flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{followUpForm.id ? 'Edit Follow-up' : 'Create Follow-up'}</h3>
                <p className="text-[11px] text-slate-500">{followUpForm.id ? 'Update the follow-up details' : 'Set the next follow-up details'}</p>
              </div>
              <button type="button" onClick={() => setFollowUpModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={saveFollowUp} className="px-3 py-2.5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Call Status</label>
                  <select value={followUpForm.callStatus} onChange={(e) => setFollowUpForm((p) => ({ ...p, callStatus: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    <option value="">Call Status</option>
                    <option value="answered">Answered</option>
                    <option value="missed">Missed</option>
                    <option value="busy">Busy</option>
                    <option value="not_connected">Not Connected</option>
                    <option value="invalid">Invalid</option>
                    <option value="disconnected">Disconnected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Outcome</label>
                  <select value={followUpForm.outcome} onChange={(e) => setFollowUpForm((p) => ({ ...p, outcome: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    <option value="">Select Outcome</option>
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="callback">Callback</option>
                    <option value="no_answer">No Answer</option>
                  </select>
                </div>
              </div>
              {followUpForm.outcome === 'callback' && (
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Next Follow-up Date & Time *</label>
                  <input type="datetime-local" value={followUpForm.nextFollowDateTime} onChange={(e) => setFollowUpForm((p) => ({ ...p, nextFollowDateTime: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" required />
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Note</label>
                <textarea rows="3" placeholder="Add follow-up notes..." value={followUpForm.note} onChange={(e) => setFollowUpForm((p) => ({ ...p, note: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setFollowUpModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-3 py-2 text-sm bg-[#009ef7] text-white rounded-lg">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[10020] flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-900">Confirm Delete</h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Are you sure you want to delete this {deleteState.type === 'contact' ? 'contact' : deleteState.type === 'followup' ? 'follow-up' : 'call'}?
              </p>
            </div>
            <div className="px-3 py-2.5 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setDeleteModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={confirmDelete} className="px-3 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Call Details</h3>
                <p className="text-[11px] text-slate-500">
                  <span className="font-medium">{selectedCallDetails?.contactId?.name || '--'}</span>
                  {selectedCallDetails?.contactId?.phone && ` � ${selectedCallDetails.contactId.phone}`}
                </p>
              </div>
              <button type="button" onClick={() => setDetailsModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="px-3 py-2.5 space-y-2.5">
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-3 py-2 border-b border-slate-100 bg-[linear-gradient(135deg,#f8fafc_0%,#eef6ff_100%)]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Call Summary</p>
                      </div>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${selectedCallDetails?.callType === 'incoming' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}>
                        {selectedCallDetails?.callType ? formatLabel(selectedCallDetails.callType) : '--'}
                      </span>
                    </div>
                  </div>
                  <div className="px-2.5 py-2 overflow-x-auto">
                    <div className="flex items-stretch gap-1.5 min-w-max">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Call Status</p>
                        <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{formatLabel(selectedCallDetails?.lastFollowUp?.callStatus) || '--'}</p>
                      </div>
                      {/*
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[92px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Source</p>
                        {selectedCallDetails?.contactId?.source ? (
                          <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                            {formatLabel(selectedCallDetails.contactId.source)}
                          </span>
                        ) : (
                          <p className="text-[11px] font-semibold text-slate-900 leading-4">--</p>
                        )}
                      </div>
                      */}
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Created</p>
                        <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{selectedCallDetails?.createdAt ? new Date(selectedCallDetails.createdAt).toLocaleDateString('en-IN') : '--'}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Updated</p>
                        <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{selectedCallDetails?.updatedAt ? new Date(selectedCallDetails.updatedAt).toLocaleDateString('en-IN') : '--'}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[176px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">{hasMeaningfulBusinessName(selectedCallDetails?.contactId?.businessName) ? 'Business' : 'Name'}</p>
                        <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">
                          {hasMeaningfulBusinessName(selectedCallDetails?.contactId?.businessName)
                            ? `${selectedCallDetails.contactId.businessName} (${selectedCallDetails?.contactId?.name || '--'})`
                            : (selectedCallDetails?.contactId?.name || '--')}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[112px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Phone</p>
                        <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">{selectedCallDetails?.contactId?.phone || '--'}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[160px]">
                        <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Email</p>
                        <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">{selectedCallDetails?.contactId?.email || '--'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Follow-up History Section */}
                <div className="rounded-lg border border-slate-200 overflow-hidden min-h-[340px] flex flex-col">
                  <div className="px-3 py-2 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                      <i className="bi bi-clock-history text-emerald-600"></i>
                      Follow-up History
                    </h4>
                    <button type="button" onClick={() => openCreateFollowUp(selectedCallDetails?._id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-200 transition">
                      <i className="bi bi-plus-circle"></i>
                      Add Follow-up
                    </button>
                  </div>

                  {detailsLoading ? (
                    <div className="p-4 text-center flex-1 flex flex-col items-center justify-center">
                      <div className="inline-block h-6 w-6 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin"></div>
                      <p className="text-xs text-slate-500 mt-1">Loading...</p>
                    </div>
                  ) : detailsFollowUps.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 flex-1 flex flex-col items-center justify-center">
                      <p className="text-xs">No follow-ups</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto flex-1 max-h-[420px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-600 font-semibold">
                          <tr>
                            <th className="px-3 py-2 border-b border-slate-200">Created Date</th>
                            <th className="px-3 py-2 border-b border-slate-200">Call Status</th>
                            <th className="px-3 py-2 border-b border-slate-200">Outcome</th>
                            <th className="px-3 py-2 border-b border-slate-200">Next Follow-up</th>
                            <th className="px-3 py-2 border-b border-slate-200">Note</th>
                            <th className="px-3 py-2 border-b border-slate-200 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {detailsFollowUps.map((item) => (
                            <tr key={item._id} className="hover:bg-slate-50 transition">
                              <td className="px-3 py-2 text-slate-700 text-xs">{formatDateTime(item?.createdAt)}</td>
                              <td className="px-3 py-2 text-center">
                                {item?.callStatus ? (
                                  <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
                                    {formatLabel(item.callStatus)}
                                  </span>
                                ) : '--'}
                              </td>
                              <td className="px-3 py-2">
                                {item?.outcome ? (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${badgeByOutcome[item.outcome] || 'bg-slate-100 text-slate-700'}`}>
                                    {outcomeLabel[item.outcome] || formatLabel(item.outcome)}
                                  </span>
                                ) : '--'}
                              </td>
                              <td className="px-3 py-2 text-slate-700 text-xs">{formatDateTime(item?.nextFollowDateTime || item?.followUpDateTime) || '--'}</td>
                              <td className="px-3 py-2 text-slate-700 max-w-xs truncate text-xs" title={item?.note || ''}>{item?.note || '--'}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button type="button" onClick={() => openEditFollowUp(item)} className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-[10px] font-semibold hover:bg-blue-200">
                                    Edit
                                  </button>
                                  <button type="button" onClick={() => openDelete('followup', item._id)} className="inline-flex items-center px-2 py-1 rounded-md bg-rose-100 text-rose-800 text-[10px] font-semibold hover:bg-rose-200">
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}      </>
    </div>
  );
};

export default CallLogs;















































